/**
 * Locale identifiers for the Nxtpath tenant console surface.
 *
 * Nxtpath is English-only. The i18n provider remains as a lightweight
 * formatting boundary because the forked console pages already route copy
 * through `useT()`, but runtime locale switching is intentionally disabled.
 */
export type Locale = 'en';

/**
 * Runtime list of supported locales, kept for forked helpers and tests.
 * Typed `readonly Locale[]` so any `.map` / `.forEach` downstream gets the
 * narrow element type.
 */
export const SUPPORTED_LOCALES: readonly Locale[] = ['en'];

/**
 * Fallback locale when no localStorage preference is present and no
 * `initialLocale` prop is forced by the test rig. Nxtpath always falls back
 * to English; stale stored locale values from the fork are ignored.
 */
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * Native-script label for the only supported locale. Kept for forked helper
 * compatibility and tests that inspect locale metadata.
 */
export const LOCALE_NATIVE_LABELS: Readonly<Record<Locale, string>> = {
  en: 'English',
};

/**
 * Shape of template parameters accepted by `t()`. Templates reference
 * params via `{name}` — see `format.ts` for the substitution rules.
 * Values coerce through `String()` so both numbers and strings work;
 * callers that need richer formatting (e.g. currency) should pre-format
 * upstream and pass the display string in.
 */
export type TParams = Readonly<Record<string, string | number>>;

/**
 * Local type guard for `Locale`. Used by the provider to validate a
 * `localStorage` read without an `as`-cast. Kept as an explicit union
 * check (rather than `SUPPORTED_LOCALES.includes(value)`) so TypeScript
 * narrows correctly without widening `SUPPORTED_LOCALES` to
 * `readonly string[]`. When a new locale is added to the `Locale`
 * union above, TypeScript reports an exhaustiveness error here via
 * the `assertNever` default branch, which forces the author to extend
 * the guard in lockstep.
 */
export function isLocale(value: unknown): value is Locale {
  if (typeof value !== 'string') {
    return false;
  }
  switch (value) {
    case 'en':
      return true;
    default:
      return false;
  }
}
